import { Outlet, Navigate, useLocation, NavigateProps } from "react-router-dom";
import { ReactNode } from "react";

function NavigateWithSearch(props: NavigateProps) {
  const { search } = useLocation();
  return <Navigate {...props} to={`${props.to}${search}`} replace />;
}

import { sentryCreateBrowserRouter } from "sentry";
import { useFirebase } from "firebase";
import { useUser } from "context/UserContext";

import Loading from "components/Loading";
import LandingLayout from "layouts/LandingLayout";
import OnboardingLayout from "layouts/OnboardingLayout";
import { PortalContent, PortalShell } from "layouts/PortalLayout";

import ErrorPage from "pages/ErrorPage";
import Form from "pages/Form";
import QuoteOptions from "pages/QuoteOptions";
import QuotePayment from "pages/QuotePayment";
import QuoteFinalize from "pages/QuoteFinalize";
import OutOfMarket from "pages/OutOfMarket";
import Home from "pages/Home";
import LoginPage from "pages/Login";
import ResetPasswordPage from "pages/ResetPassword";
import EmailActionPage from "pages/EmailAction";
import AccountPage from "pages/Account";
import PolicyDetailsPage from "pages/PolicyDetails";
import CaseDetailsPage from "pages/CaseDetails";
import KnowledgeBasePage from "pages/KnowledgeBase";
import ConsultationSchedulingPage from "pages/ConsultationScheduling";
import JoinPage from "pages/Join";
import SetPasswordPage from "pages/SetPassword";
import AuthVerifyPage from "pages/AuthVerify";
import AuthTokenHandlerPage from "pages/AuthTokenHandler";
import SavedQuotePage from "pages/SavedQuote";
import LegacyQuoteRedirect from "pages/SavedQuote/LegacyQuoteRedirect";
import QuoteFlowLoadingPage from "pages/QuoteFlow/QuoteFlowLoadingPage";
import StartResumePage from "pages/StartResume";
import StartLeadPage from "pages/StartLead";
import StartVerifyPage from "pages/StartVerify";
import QuoteFlowPage from "pages/QuoteFlow";
import { BasecampContainer } from "layouts/OnboardingLayout/OnboardingLayout";
import Header from "components/Header";
import Footer from "components/Footer";
import { SignalPageTracker } from "context/SignalContext";
import ArrangementFlow from "pages/ArrangementFlow";
import PackageAndSelectionsStep from "pages/ArrangementFlow/PackageAndSelectionsStep";
import ServicesStep from "pages/ArrangementFlow/ServicesStep";
import CompletionStep from "pages/ArrangementFlow/CompletionStep";

// TODO: Make this new home page
// If unauthenticated, redirect to login page
// If authenticated, show home page dashboard
const Root = () => {
  return (
    <>
      <SignalPageTracker />
      <Outlet />
    </>
  );
};

function AuthRequired({
  children,
  requireIdentified = false,
  portalShell = false,
  quoteFlowShell = false,
}: {
  children: ReactNode;
  // When true, also wait for PostHog to finish identifying the user and
  // reload feature flags before rendering. Use on routes whose initial
  // render branches on a feature flag, so the flag isn't evaluated against
  // the anonymous distinct id while `/me` is still in flight.
  requireIdentified?: boolean;
  // When true, the loading state renders in the portal content column
  // instead of the top-header basecamp shell. Use on routes under the
  // portal shell route, which supplies the chrome.
  portalShell?: boolean;
  // When true, the loading state is the quote flow's loading page. Use on
  // routes that sit between the lead form and the quote.
  quoteFlowShell?: boolean;
}) {
  const { user } = useFirebase();
  // Pull `loading` from `useUser` rather than `useFirebase` so it covers
  // both the Firebase auth handshake AND the `/me` fetch — without that,
  // a `requireIdentified` route can render in the window where Firebase
  // has resolved but `/me` is still in flight, with `posthogFlagsReady`
  // still on its anonymous default.
  const { loading, posthogFlagsReady, posthogIdentityStatus } = useUser();
  const location = useLocation();
  const waitingForPostHogIdentity =
    requireIdentified &&
    user &&
    posthogIdentityStatus !== "failed" &&
    !posthogFlagsReady;

  if (loading || waitingForPostHogIdentity) {
    if (quoteFlowShell) return <QuoteFlowLoadingPage />;

    if (portalShell) {
      return (
        <PortalContent>
          <Loading message="Loading..." />
        </PortalContent>
      );
    }

    return (
      <BasecampContainer>
        <Header basecamp />
        <div className="mt-16 w-full px-3 sm:px-4">
          <Loading message="Loading..." />
        </div>
        <Footer />
      </BasecampContainer>
    );
  }

  if (!user) {
    // Only add redirect param if we're not already going to the default path
    const redirectPath =
      location.pathname === "/"
        ? "/login"
        : `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}

export default sentryCreateBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: (
      <LandingLayout>
        <ErrorPage />
      </LandingLayout>
    ),
    children: [
      {
        path: "forms/:formId/:responseId",
        element: <Form />,
      },
      // Dev-only design preview. The import sits inside the __DEV__ branch and
      // is dynamic, so a production build drops the branch and never reaches
      // the module — nothing it pulls in, fixtures or assets, ends up in the
      // production graph.
      ...(__DEV__
        ? [
            {
              path: "preview/death-certificates",
              lazy: async () => ({
                Component: (await import("pages/ArrangementPreview")).default,
              }),
            },
          ]
        : []),
      {
        path: "continue",
        element: (
          <OnboardingLayout>
            <Outlet />
          </OnboardingLayout>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/" />,
          },
          {
            path: "pre-planning",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to="/" />,
              },
              {
                path: "quote",
                element: <QuoteOptions />,
              },
              {
                path: "payment",
                element: <QuotePayment />,
              },
              {
                path: "finalize",
                element: <QuoteFinalize />,
              },
              {
                path: "out-of-market",
                element: <OutOfMarket />,
              },
            ],
          },
        ],
      },

      {
        path: "join/:token",
        element: <JoinPage />,
      },

      {
        path: "quote/:quoteId",
        element: <LegacyQuoteRedirect />,
      },
      {
        path: "quote/:quoteId/saved",
        element: <SavedQuotePage />,
      },
      {
        // The pre-need online flow: a minted quote link, ?t= carrying the
        // access token. Plural on purpose — distinct from the saved-quote
        // page above. One route per station (and per walk screen under
        // the application): the segments name the screen, and the flow's
        // landing rule caps what a URL may ask for.
        path: "quotes/:quoteId/:step?/:screenRef?",
        element: <QuoteFlowPage />,
      },

      {
        // Public: a lead sent to verify has no account session yet. The
        // static segment outranks `start/:step?`.
        path: "start/verify",
        element: <StartVerifyPage />,
      },

      {
        path: "start/resume",
        element: (
          <AuthRequired quoteFlowShell>
            <StartResumePage />
          </AuthRequired>
        ),
      },

      {
        // Public: the lead form opens before any account exists. The static
        // `start/resume` and `start/verify` routes outrank this route's `:step`.
        path: "start/:step?",
        element: <StartLeadPage />,
      },

      {
        path: "email-action",
        element: <EmailActionPage />,
      },

      {
        path: "auth/verify-email",
        element: <AuthVerifyPage />,
      },

      {
        path: "auth/magic-link",
        element: <AuthTokenHandlerPage />,
      },

      {
        path: "login",
        element: <LoginPage />,
      },

      {
        path: "reset-password",
        element: <ResetPasswordPage />,
      },

      {
        path: "set-password",
        element: (
          <AuthRequired>
            <SetPasswordPage />
          </AuthRequired>
        ),
      },
      {
        element: <PortalShell />,
        children: [
          {
            index: true,
            element: (
              <AuthRequired portalShell>
                <Home />
              </AuthRequired>
            ),
          },

          {
            path: "cases/:caseId/arrange",
            element: (
              <AuthRequired requireIdentified portalShell>
                <ArrangementFlow />
              </AuthRequired>
            ),
            children: [
              { index: true, element: <NavigateWithSearch to="packages" /> },
              { path: "packages", element: <PackageAndSelectionsStep /> },
              {
                path: "urns-and-keepsakes",
                element: <NavigateWithSearch to="../packages" />,
              },
              { path: "services", element: <ServicesStep /> },
              { path: "confirmation", element: <CompletionStep /> },
              {
                path: "completion",
                element: <NavigateWithSearch to="../confirmation" />,
              },
            ],
          },

          {
            path: "policies/:policyId",
            element: (
              <AuthRequired portalShell>
                <PolicyDetailsPage />
              </AuthRequired>
            ),
          },

          {
            path: "policies/:policyId/answers",
            element: (
              <AuthRequired portalShell>
                <KnowledgeBasePage />
              </AuthRequired>
            ),
          },

          {
            path: "cases/:caseId/step/schedule-consultation",
            element: (
              <AuthRequired requireIdentified portalShell>
                <ConsultationSchedulingPage />
              </AuthRequired>
            ),
          },

          {
            path: "cases/:caseId",
            element: (
              <AuthRequired requireIdentified portalShell>
                <CaseDetailsPage />
              </AuthRequired>
            ),
          },

          {
            path: "cases/:caseId/answers",
            element: (
              <AuthRequired portalShell>
                <KnowledgeBasePage />
              </AuthRequired>
            ),
          },

          {
            path: "account",
            element: (
              <AuthRequired portalShell>
                <Outlet />
              </AuthRequired>
            ),
            children: [
              {
                index: true,
                element: <AccountPage />,
              },
            ],
          },
        ],
      },
    ],
  },
]);
